import { useEffect, useRef, useState } from 'react'
import type { ContentOf } from '@shared/schemas/exercise'
import type { SpeakingAttemptResult } from '@shared/types/speaking'
import { Button } from '@web/components/ui/Button'
import { useAudio } from '@web/lib/audio'
import { api } from '@web/lib/api'
import { isRecordingSupported, Recorder, RecorderFailure } from '@web/lib/recorder'

/**
 * Speaking practice.
 *
 * The screen is built around one honesty rule: it shows her exactly what the
 * transcriber heard, and grades only *which words came through*. It never
 * reports a percentage for pronunciation or accent, because speech-to-text
 * cannot measure those — and a confident wrong score about how she sounds is
 * worse than no score, since it teaches her to distrust everything else here.
 */
export function SpeakingExercise({
  exerciseId,
  content,
  onResult,
}: {
  exerciseId: string
  content: ContentOf<'speaking'>
  onResult?: (result: SpeakingAttemptResult) => void
}) {
  const { play, resolve } = useAudio()
  // Resolved from the lesson's audio map rather than a hand-authored media id:
  // the same mechanism every other Portuguese string on screen uses, so a
  // speaking exercise gets its model clip for free.
  const referenceId = content.referenceMediaId ?? resolve(content.expectedText)
  const recorderRef = useRef<Recorder | null>(null)
  const [state, setState] = useState<'idle' | 'recording' | 'sending'>('idle')
  const [level, setLevel] = useState(0)
  const [error, setError] = useState<string | null>(null)
  const [result, setResult] = useState<SpeakingAttemptResult | null>(null)

  // A component that unmounts mid-recording must still release the
  // microphone, or the browser keeps showing the recording indicator.
  useEffect(() => () => recorderRef.current?.cleanup(), [])

  const supported = isRecordingSupported()

  async function begin() {
    setError(null)
    setResult(null)
    const recorder = new Recorder()
    recorderRef.current = recorder
    try {
      await recorder.start(setLevel)
      setState('recording')
    } catch (failure) {
      setState('idle')
      setError(describe(failure))
    }
  }

  async function finish() {
    const recorder = recorderRef.current
    if (!recorder) return
    setState('sending')
    try {
      const recording = await recorder.stop()
      const response = await api<SpeakingAttemptResult>(
        `/speaking/attempts?exerciseId=${encodeURIComponent(exerciseId)}`,
        { method: 'POST', body: recording.blob, contentType: recording.contentType },
      )
      setResult(response)
      onResult?.(response)
    } catch {
      setError('That did not upload. Check your connection and try again.')
    } finally {
      setState('idle')
      setLevel(0)
    }
  }

  if (!supported) {
    return (
      <div className="space-y-3 rounded-card bg-sand-100 px-4 py-5">
        <p className="text-lg text-ink-900" lang="pt-BR">
          {content.expectedText}
        </p>
        <p className="text-sm text-ink-500">
          This browser cannot record audio. Open VIVA in Safari or Chrome to practise speaking.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="rounded-card bg-white p-5 text-center ring-1 ring-brand-900/5">
        <p className="text-xs font-semibold tracking-[0.16em] text-brand-500 uppercase">
          Say this
        </p>
        <div className="mt-2 flex items-center justify-center gap-3">
          <p className="font-display text-2xl font-bold text-ink-900" lang="pt-BR">
            {content.expectedText}
          </p>
          {referenceId && (
            <button
              type="button"
              onClick={() => play(referenceId)}
              aria-label={`Listen: ${content.expectedText}`}
              className="flex size-10 shrink-0 items-center justify-center rounded-full bg-brand-100 text-brand-600 hover:bg-brand-200"
            >
              ▶
            </button>
          )}
        </div>
      </div>

      {result ? (
        <Feedback result={result} onRetry={() => setResult(null)} />
      ) : (
        <div className="flex flex-col items-center gap-3">
          <button
            type="button"
            onClick={() => (state === 'recording' ? void finish() : void begin())}
            disabled={state === 'sending'}
            aria-label={state === 'recording' ? 'Stop recording' : 'Start recording'}
            className={`relative flex size-20 items-center justify-center rounded-full transition-colors disabled:opacity-60 ${
              state === 'recording' ? 'bg-clay-500 text-white' : 'bg-brand-600 text-white'
            }`}
          >
            {/* The ring scales with input level, so she can see the mic is
                actually hearing her before she finds out it was muted. */}
            {state === 'recording' && (
              <span
                aria-hidden="true"
                className="absolute inset-0 rounded-full bg-clay-500/30"
                style={{ transform: `scale(${1 + level * 0.6})` }}
              />
            )}
            <MicIcon busy={state === 'sending'} />
          </button>

          <p aria-live="polite" className="text-sm text-ink-500">
            {state === 'recording'
              ? 'Listening — tap to stop'
              : state === 'sending'
                ? 'Checking what you said…'
                : 'Tap and say it out loud'}
          </p>
        </div>
      )}

      {error && (
        <p role="alert" className="rounded-xl bg-clay-50 px-4 py-3 text-sm text-clay-700">
          {error}
        </p>
      )}
    </div>
  )
}

function Feedback({
  result,
  onRetry,
}: {
  result: SpeakingAttemptResult
  onRetry: () => void
}) {
  const { feedback } = result
  const tone = feedback.passed
    ? 'bg-brand-100 text-brand-800'
    : 'bg-cream text-ink-700 ring-1 ring-gold-300'

  return (
    <div className="space-y-4">
      <div role="status" className={`rounded-xl px-4 py-3 ${tone}`}>
        <p className="font-display font-bold">{feedback.message}</p>

        {feedback.transcript && (
          <p className="mt-2 text-sm">
            We heard: <span lang="pt-BR">“{feedback.transcript}”</span>
          </p>
        )}

        {/* Word-level, because "78%" tells her nothing she can act on, but a
            highlighted missing word tells her exactly what to try again. */}
        <p className="mt-3 flex flex-wrap gap-x-1.5 gap-y-1" lang="pt-BR">
          {feedback.diff
            .filter((entry) => entry.expected !== undefined)
            .map((entry, index) => (
              <span
                key={index}
                className={
                  entry.status === 'correct' || entry.status === 'accent'
                    ? 'font-semibold text-brand-700'
                    : 'text-clay-700 underline decoration-clay-300 decoration-2 underline-offset-4'
                }
              >
                {entry.expected}
              </span>
            ))}
        </p>
      </div>

      <p className="text-xs text-ink-400">
        This checks which words came through, not your accent — that is something
        only a person can judge.
      </p>

      <div className="flex gap-2">
        <Button variant="secondary" onClick={onRetry} className="flex-1">
          Try again
        </Button>
        {result.xpEarned > 0 && (
          <span className="flex items-center rounded-full bg-brand-100 px-3 text-sm font-semibold text-brand-700">
            +{result.xpEarned} XP
          </span>
        )}
      </div>
    </div>
  )
}

function describe(failure: unknown): string {
  if (failure instanceof RecorderFailure) {
    switch (failure.reason) {
      case 'permission-denied':
        return 'VIVA needs microphone access. Allow it in your browser settings and try again.'
      case 'no-microphone':
        return 'No microphone found.'
      case 'unsupported':
        return 'This browser cannot record audio.'
      default:
        return 'The recording could not start. Try again.'
    }
  }
  return 'The recording could not start. Try again.'
}

function MicIcon({ busy }: { busy: boolean }) {
  if (busy) {
    return (
      <span className="size-6 animate-spin rounded-full border-2 border-white border-t-transparent" />
    )
  }
  return (
    <svg viewBox="0 0 24 24" fill="none" className="size-8" aria-hidden="true">
      <rect x="9" y="3" width="6" height="11" rx="3" fill="currentColor" />
      <path
        d="M5.5 11.5a6.5 6.5 0 0 0 13 0M12 18v3"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  )
}
